// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { FormTextField } from '@/components/form-text-field';
import { SupportSnackbar } from '@/components/support-snackbar';
import { Ticket, ticketDescriptionInputSchema } from '@/ticket/api/types';
import { useUpdateTicket } from '@/ticket/hooks/use-update-ticket';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { SubmitHandler, useForm } from 'react-hook-form';
import { z } from 'zod';

const formSchema = z.object({
  description: ticketDescriptionInputSchema.min(1, 'Description is required'),
});

type FormData = z.infer<typeof formSchema>;

interface Props {
  isOpen: boolean;
  ticket: Ticket;
  onClose: () => void;
}

export function TicketDescriptionDialog(props: Props) {
  const { control, handleSubmit, reset } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: { description: props.ticket.description },
  });

  const queryClient = useQueryClient();
  const updateTicketMutation = useUpdateTicket(props.ticket.key, {
    onSuccess: (data) => {
      queryClient.setQueryData(['tickets', props.ticket.key], data);
      reset({
        description: data.description,
      });
      props.onClose();
    },
  });

  const handleValidSubmission: SubmitHandler<FormData> = (data) => {
    updateTicketMutation.mutate({ description: data.description.trim() });
  };

  const close = () => {
    props.onClose();
  };

  return (
    <>
      <SupportSnackbar
        isOpen={updateTicketMutation.isError}
        message="An error occurred while updating the ticket."
        onClose={updateTicketMutation.reset}
      />
      <Dialog
        fullWidth
        maxWidth="xs"
        open={props.isOpen}
        onClose={close}
        PaperProps={{
          component: 'form',
          onSubmit: handleSubmit(handleValidSubmission),
        }}
      >
        <DialogTitle>Edit description</DialogTitle>
        <DialogContent dividers>
          <FormTextField
            fullWidth
            isMultiline
            name="description"
            minimumRows={4}
            label="Description"
            control={control}
            disabled={updateTicketMutation.isPending}
          />
        </DialogContent>
        <DialogActions>
          <Button
            size="small"
            onClick={close}
            disabled={updateTicketMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            size="small"
            type="submit"
            color="secondary"
            variant="contained"
            disabled={updateTicketMutation.isPending}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
